import { useState } from "react";
import { showToast } from "../../helper/ShowToast";
import { TagIcon, User2Icon } from "lucide-react";
import { ToastContainer } from "react-toastify";

export const ArtistInviteForm = ({ mutateInvite, onSuccess }) => {
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState(""); // "idle" | "loading" | "success" | "error"
    const [errorMsg, setErrorMsg] = useState("");
    const [isArtist, setIsArtist] = useState(null);

    const validate = () => {
        if (!email.trim()) return "Email is required.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
            return "Please enter a valid email address.";
        return null;
    };
    const { isPending } = mutateInvite

    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setErrorMsg(validationError);
            return;
        }

        setErrorMsg("");
        setStatus("loading");
        mutateInvite.mutate({ email: email, accountType: isArtist ? "Artist" : "Label" }, {
            onSuccess: (data) => {
                onSuccess && onSuccess(data?.message || "Request submitted successfully!");
                // showToast("success", "Invite request submitted successfully!");
            },
            onError: (error) => {
                if (error.status === 409) {
                    showToast("error", error?.response?.data?.error || "Failed to submit invite request");
                    setEmail('')
                } else {
                    showToast("error", error?.response?.data?.message || "Failed to submit invite request");
                }

            }
        })
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSubmit();
    };

    return (
        <div className="w-full py-6 font-sans">
            <ToastContainer />
            <div className="w-full max-w-2xl bg-white border border-black/10 rounded-2xl p-4">

                {/* Beta badge */}
                <div className="inline-flex items-center gap-1.5 bg-gray-100 border border-black/[0.08] rounded-full px-3 py-1 text-[11px] font-medium text-gray-500 tracking-[0.08em] uppercase">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                    Beta Access
                </div>

                <div className="text-theme_clr1">
                    <p className="text-lg text-center leading-relaxed mb-2">
                        What describes you best?
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                        <button
                            onClick={() => setIsArtist(true)}
                            className={`flex items-center justify-center gap-3 p-3 rounded-2xl text-xl font-medium transition-all ${isArtist === true
                                ? 'bg-white text-theme_clr2 border-4 border-[#FA6138]'
                                : 'bg-white text-theme_clr1 shadow-xl shadow-orange-500/80 border-4 border-transparent'
                                }`}
                        >
                            <User2Icon size={28} className={`${isArtist === true ? 'text-theme_clr2' : 'text-theme_clr1'}`} />
                            Artist
                        </button>

                        <button
                            onClick={() => setIsArtist(false)}
                            className={`flex items-center justify-center gap-3 p-3 rounded-2xl text-xl font-medium transition-all ${isArtist === false
                                ? 'bg-white text-theme_clr2 border-4 border-[#FA6138]'
                                : 'bg-white text-theme_clr1 shadow-xl shadow-orange-500/80 border-4 border-transparent'
                                }`}
                        >
                            <TagIcon size={28} className={`${isArtist === false ? 'text-theme_clr2' : 'text-theme_clr1'}`} />
                            Label
                        </button>
                    </div>
                </div>

                {/* Heading */}
                <p className="text-sm text-theme_clr1 text-center leading-relaxed mt-4 mb-2">
                    Fill in your details below and we'll review your request for distribution access.
                </p>

                {/* Email field */}
                <div className="mb-6">
                    <label
                        htmlFor="artist-email"
                        className="block text-[12px] font-medium tracking-[0.06em] uppercase text-theme_clr1 mb-2"
                    >
                        Artist Email
                    </label>
                    <input
                        id="artist-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (errorMsg) setErrorMsg("");
                            if (status === "error") setStatus("idle");
                        }}
                        onKeyDown={handleKeyDown}
                        className={`w-full h-11 rounded-lg border bg-gray-50 text-gray-900 text-[15px] px-3.5 outline-none transition-colors focus:bg-white focus:border-theme_clr1 placeholder:text-gray-300 ${errorMsg ? "border-red-400" : "border-black/[0.18]"
                            }`}
                    />
                    {errorMsg && (
                        <p className="text-xs text-red-500 mt-1.5">{errorMsg}</p>
                    )}
                </div>

                {/* CTA */}
                <button
                    onClick={handleSubmit}
                    disabled={isPending}
                    className="w-full h-11 bg-theme_clr2 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 mb-3 transition-opacity hover:opacity-85 active:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {isPending ? <Spinner /> : "Request Invite"}
                </button>

                {/* Footer note */}
                <p className="text-sm text-theme_clr1 text-center leading-relaxed">
                    Invite-only access. We'll review your request and send an invite when
                    approved.
                </p>
            </div>
        </div>
    );
}

function Spinner() {
    return (
        <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin inline-block" />
    );
}